import { GameContextBuilder } from './game-context-builder';
import { PluginRegistry } from './plugin-registry';
export class PluginLoader {
    constructor(opts = {}) {
        this._active = null;
        this._activating = null;
        this._builder = opts.builder ?? new GameContextBuilder(opts);
    }
    async activate(gameId, containerEl, onExitRequested) {
        // Serialise concurrent calls so React 19 Strict Mode's double-effect doesn't
        // race through before this._active is set, causing two canvases to mount.
        if (this._activating) {
            try {
                await this._activating;
            }
            catch {
                /* previous activation failed; fall through to start fresh */
            }
        }
        // Same game, same container is already fully started (e.g. Strict Mode
        // re-mount, which reuses the same DOM node across its double-invoke) — no-op.
        // The containerEl check matters: a genuine unmount+remount (e.g. the player
        // leaves and re-enters the same game before the deferred destroy below has
        // fired) hands activate() a brand-new container element, so this must fall
        // through to a real pause+destroy+restart rather than silently no-op against
        // a container the plugin was never attached to.
        if (this._active?.gameId === gameId && this._active.containerEl === containerEl)
            return;
        if (this._active) {
            this._active.plugin.pause();
            await this._active.plugin.destroy();
            this._active = null;
        }
        this._activating = (async () => {
            const manifest = PluginRegistry.getById(gameId);
            if (!manifest)
                throw new Error(`No plugin registered for gameId: "${gameId}"`);
            const { default: PluginClass } = await manifest.factory();
            const plugin = new PluginClass();
            this._active = { plugin, gameId, containerEl };
            const context = this._builder.build(gameId, onExitRequested);
            await plugin.initialize(context);
            await plugin.start(containerEl);
        })();
        try {
            await this._activating;
        }
        finally {
            this._activating = null;
        }
    }
    async deactivate() {
        if (!this._active)
            return;
        this._active.plugin.pause();
        await this._active.plugin.destroy();
        this._active = null;
    }
    /**
     * Pauses the active plugin without destroying it (used before the 500ms
     * destroy delay). Returns the paused plugin instance so a caller can later
     * hand it to {@link destroyIfStillActive}, guarding against destroying a
     * newer activation that started during the delay.
     */
    pauseActive() {
        const plugin = this._active?.plugin ?? null;
        plugin?.pause();
        return plugin;
    }
    /** Destroys the already-paused active plugin and clears the active slot. */
    async destroyActive() {
        if (!this._active)
            return;
        await this._active.plugin.destroy();
        this._active = null;
    }
    /**
     * Destroys `plugin` only if it is still this loader's active instance —
     * a no-op otherwise. Used for a deferred destroy scheduled by a caller
     * (e.g. GameScreen's DESTROY_DELAY_MS): if the player re-enters the same
     * game before the timer fires, `activate()` will have already replaced
     * `_active` with a fresh instance attached to the new container, and the
     * stale timer must not tear that fresh instance down instead.
     */
    async destroyIfStillActive(plugin) {
        if (!plugin || this._active?.plugin !== plugin)
            return;
        await plugin.destroy();
        this._active = null;
    }
    get activeGameId() {
        return this._active?.gameId ?? null;
    }
}
